import {
  Button,
  ButtonProps,
  Dialog,
  EyeIcon,
  Flex,
  IconButton,
  Image,
  Text,
  Tooltip,
  XMarkIcon,
} from 'components'
import { EmbeddedExplorerProps } from 'desktop-app/src/desktopTypes'
import { Suspense, lazy, useRef, useState } from 'react'
import { useWindowSize } from 'usehooks-ts'
import ipadOutline from '../assets/device-outline-ipad.png'
import tvOutline from '../assets/device-outline-tv.png'

const EmbeddedExplorer = lazy(() =>
  import('desktop-app').then(m => ({ default: m.EmbeddedExplorer }))
)

interface Props extends Omit<ButtonProps, 'onClick'>, EmbeddedExplorerProps {
  /** default desktop */
  device?: Device
  label?: string
  previewTitle: string
}

type Device = 'desktop' | 'ipad'

export default function TemplatePreviewButton({
  device = 'desktop',
  eventId,
  facilityId,
  label,
  momentId,
  previewTitle,
  template,
  templateId,
  ...rest
}: Props) {
  const portalContainerRef = useRef<HTMLElement | null>(null)
  const { height = 1, width = 0 } = useWindowSize()
  const [showPreview, setShowPreview] = useState(false)

  if (typeof document !== 'undefined') portalContainerRef.current = document.body

  if (!eventId && !facilityId && !template && !templateId) return null

  const dimensions = getDimensions(device, width, height)
  const { border, deviceHeight, deviceWidth, headerHeight, radius, xOffset, yOffset } = dimensions

  return (
    <>
      {label ? (
        <Button
          palette="primary"
          leftIcon={<EyeIcon type="solid" />}
          onClick={() => setShowPreview(s => !s)}
          {...rest}
        >
          {label}
        </Button>
      ) : (
        <Tooltip content="Preview">
          <IconButton
            aria-label="preview-button"
            icon={<EyeIcon type="solid" />}
            onClick={() => setShowPreview(s => !s)}
            {...rest}
          />
        </Tooltip>
      )}

      <Dialog
        bodyProps={{ display: 'flex', flex: 1, overflow: 'hidden' }}
        contentProps={{
          backdropFilter: 'blur(10px)',
          background: '#FFFFFF77',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          maxHeight: 'none',
          width: '100%',
        }}
        hideClose
        motionPreset="none"
        onClose={() => setShowPreview(s => !s)}
        open={showPreview}
        portalProps={{ container: portalContainerRef }}
        size="full"
      >
        <Flex
          align="center"
          flex={1}
          justify="center"
          onClick={() => setShowPreview(s => !s)}
          position="relative"
          userSelect="none"
        >
          <Flex direction="column" height={deviceHeight + headerHeight * 2} width={deviceWidth}>
            <Flex height={headerHeight} justify="space-between">
              <Text textStyle="h3">{previewTitle}</Text>
              <IconButton aria-label="close-preview-button" icon={<XMarkIcon size="lg" />} />
            </Flex>
            <Image
              height="100%"
              src={device === 'desktop' ? tvOutline : ipadOutline}
              width="100%"
            />
            <Flex height={headerHeight} />
          </Flex>

          <Flex
            background="gray.800"
            borderRadius={radius}
            onClick={e => e.stopPropagation()}
            boxShadow="dark-lg"
            height={deviceHeight - border * 2 + yOffset}
            left={(width - deviceWidth + xOffset) / 2 + border}
            overflow="hidden"
            position="absolute"
            top={(height - deviceHeight + yOffset) / 2 + border}
            width={deviceWidth - border * 2 + xOffset}
          >
            <Suspense>
              <EmbeddedExplorer
                eventId={eventId}
                facilityId={facilityId}
                momentId={momentId}
                preview
                template={template}
                templateId={templateId}
              />
            </Suspense>
          </Flex>
        </Flex>
      </Dialog>
    </>
  )
}

function getDimensions(device: Device, width: number, height: number) {
  const windowRatio = width / height

  const deviceRatio = device === 'desktop' ? 1.7 : 1.3
  const deviceWidth = (windowRatio > deviceRatio ? height * deviceRatio : width) * 0.8
  const deviceHeight = deviceWidth / deviceRatio
  const headerHeight = 60
  const xOffset = 0 * deviceWidth
  const yOffset = (device === 'desktop' ? -0.026 : 0) * deviceHeight
  const border = (device === 'desktop' ? 0.014 : 0.05) * deviceHeight
  const radius = device === 'desktop' ? 0 : 0.015 * deviceHeight

  return { border, deviceHeight, deviceWidth, headerHeight, radius, xOffset, yOffset }
}
